// Terrain height (GEN-4). A continuous function of the climate fields — spline
// over continentalness for the base elevation, erosion scaling the relief, ridges
// pushing peaks, plus a small seam-free detail term. Mountains (high
// continentalness) add high-frequency jaggedness, the only place the surface may
// step more than a few blocks between neighbors (the no-seam test allows it).
import { Simplex, fbm2 } from '../../engine/noise';
import { CLIMATE, WORLD } from '../../config';
import type { Climate } from './climate';
import type { TerrainSpline } from '../../registry/schemas';

/** Piecewise-linear spline over sorted [x, y] control points. */
export function evalSpline(points: ReadonlyArray<readonly [number, number]>, x: number): number {
  const first = points[0]!;
  if (x <= first[0]) return first[1];
  const last = points[points.length - 1]!;
  if (x >= last[0]) return last[1];
  for (let i = 1; i < points.length; i++) {
    const p1 = points[i]!;
    if (x <= p1[0]) {
      const p0 = points[i - 1]!;
      const t = (x - p0[0]) / (p1[0] - p0[0]);
      return p0[1] + (p1[1] - p0[1]) * t;
    }
  }
  return last[1];
}

export class TerrainShaper {
  private readonly detail: Simplex;
  private readonly jag: Simplex;

  constructor(seed: number | string | bigint, private readonly spline: TerrainSpline) {
    this.detail = new Simplex(`${seed}#detail`);
    this.jag = new Simplex(`${seed}#jagged`);
  }

  heightAt(x: number, z: number, c: Climate): number {
    const base = evalSpline(this.spline.continental, c.continentalness);
    const relief = evalSpline(this.spline.erosion, c.erosion);
    const detail = fbm2(this.detail, x * CLIMATE.DETAIL_FREQ, z * CLIMATE.DETAIL_FREQ, { octaves: 3 }) * 7 * relief;
    const ridgePeak = Math.pow(Math.max(0, c.ridges), 2) * 22 * relief;
    let h = base + detail + ridgePeak;
    if (c.continentalness > 0.35) {
      const jag = fbm2(this.jag, x * CLIMATE.JAGGED_FREQ, z * CLIMATE.JAGGED_FREQ, { octaves: 3 });
      h += Math.max(0, jag) * (c.continentalness - 0.35) * 90;
    }
    return Math.max(WORLD.MIN_Y + 8, Math.min(WORLD.MAX_Y - 40, Math.floor(h)));
  }
}
